/**
 * ============================================================================
 * ADMIN DASHBOARD PAGE
 * ============================================================================
 * 
 * Main Admin Dashboard
 */

/**
 * ============================================================================
 * ADMIN DASHBOARD PAGE - 100% Ready
 * ============================================================================
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../../hooks/useAdmin';
import { useAdminStore } from '../../store';
import { useAuthContext } from '../../providers';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import AdminLayout from '../../components/admin/AdminLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import StatusBadge from '../../components/common/StatusBadge';

/**
 * Admin Dashboard Component
 * @returns {JSX.Element}
 */
const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthContext();
  const { dashboardMetrics, loading } = useAdminStore();
  const { loadDashboardMetrics } = useAdmin();
  const { showError } = useToast();
  const { formatCurrency, formatNumber, language } = useI18n();

  // Check if user is admin
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    if (user?.role !== 'admin' && user?.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    const loadData = async () => {
      try {
        await loadDashboardMetrics();
      } catch (error) {
        showError('Failed to load dashboard metrics');
        console.error('Load metrics error:', error);
      }
    };

    if (isAuthenticated && (user?.role === 'admin' || user?.role === 'super_admin')) {
      loadData();
    }
  }, [isAuthenticated, user, loadDashboardMetrics, showError]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Spinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  if (!dashboardMetrics) {
    return (
      <AdminLayout>
        <Card variant="glass" className="p-12 text-center">
          <p className="text-gray-600 dark:text-gray-400">
            {language === 'fr' ? 'Aucune donnée disponible' : 'No data available'}
          </p>
        </Card>
      </AdminLayout>
    );
  }

  const metrics = dashboardMetrics;

  const metricCards = [
    {
      label: language === 'fr' ? 'Volume total' : 'Total Volume',
      value: formatCurrency(metrics.totalVolume || 0),
      change: metrics.volumeChange || 0,
      icon: '💰',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      label: language === 'fr' ? 'Transactions actives' : 'Active Transactions',
      value: formatNumber(metrics.activeTransactions || 0),
      change: metrics.activeTransactionsChange || 0,
      icon: '📈',
      color: 'from-green-500 to-emerald-500',
    },
    {
      label: language === 'fr' ? 'Litiges actifs' : 'Active Disputes',
      value: formatNumber(metrics.activeDisputes || 0),
      change: metrics.disputesChange || 0,
      icon: '⚠️',
      color: 'from-red-500 to-pink-500',
      negative: true,
    },
    {
      label: language === 'fr' ? 'Nouveaux utilisateurs' : 'New Users',
      value: formatNumber(metrics.newUsers || 0),
      change: metrics.newUsersChange || 0,
      icon: '👥',
      color: 'from-purple-500 to-pink-500',
    },
    {
      label: language === 'fr' ? 'Complétées' : 'Completed',
      value: formatNumber(metrics.completedTransactions || 0),
      change: metrics.completedChange || 0,
      icon: '✅',
      color: 'from-green-500 to-teal-500',
    },
    {
      label: language === 'fr' ? 'Revenus' : 'Revenue',
      value: formatCurrency(metrics.revenue || 0),
      change: metrics.revenueChange || 0,
      icon: '💳',
      color: 'from-yellow-500 to-orange-500',
    },
  ];

  return (
    <AdminLayout>
      {/* Overview */}
      <div className="mb-8 animate-fadeInUp">
        <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-6 flex items-center gap-3">
          <span className="text-3xl">📊</span>
          {language === 'fr' ? 'Vue d\'ensemble' : 'Overview'} - {new Date().toLocaleDateString()}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {metricCards.map((metric, index) => (
            <Card
              key={index}
              variant="glass"
              hover
              className="animate-fadeInUp"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm font-bold text-gray-600 dark:text-gray-400">
                  {metric.label}
                </div>
                <div
                  className={`w-12 h-12 rounded-xl bg-gradient-to-br ${metric.color} flex items-center justify-center text-2xl shadow-lg`}
                >
                  {metric.icon}
                </div>
              </div>
              <div className="text-3xl font-black text-gray-900 dark:text-white mb-2">
                {metric.value}
              </div>
              <div
                className={`text-xs font-bold flex items-center gap-1 ${
                  metric.negative && metric.change < 0
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-green-600 dark:text-green-400'
                }`}
              >
                <span>{metric.change >= 0 ? '↑' : '↓'}</span>
                {metric.change >= 0 ? '+' : ''}
                {metric.change}% {language === 'fr' ? 'vs hier' : 'vs yesterday'}
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Urgent Actions */}
      {metrics.urgentActions && metrics.urgentActions.length > 0 && (
        <div className="mb-8 animate-fadeInUp" style={{ animationDelay: '0.6s' }}>
          <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-6 flex items-center gap-3">
            <span className="text-3xl">🚨</span>
            {language === 'fr' ? 'Actions urgentes requises' : 'Urgent Actions Required'} (
            {metrics.urgentActions.length})
          </h2>
          <div className="space-y-4">
            {metrics.urgentActions.map((action, index) => (
              <Card
                key={index}
                variant="outlined"
                className="border-2 border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 animate-fadeInUp"
                style={{ animationDelay: `${0.7 + index * 0.1}s` }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-2xl">{action.icon}</span>
                      <h3 className="font-black text-gray-900 dark:text-white text-lg">
                        {action.title}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                      {action.description}
                    </p>
                    {action.timeRemaining && (
                      <StatusBadge variant="error" size="sm">
                        {action.timeRemaining}
                      </StatusBadge>
                    )}
                  </div>
                  <Button
                    variant="gradient"
                    size="sm"
                    onClick={() => navigate(action.actionPath)}
                    className="shadow-xl hover:shadow-2xl transition-all ml-4"
                  >
                    {action.actionLabel} →
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <Card variant="glass" className="animate-fadeInUp" style={{ animationDelay: '0.8s' }}>
        <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-6 flex items-center gap-3">
          <span className="text-3xl">🔥</span>
          {language === 'fr' ? 'Activité récente' : 'Recent Activity'}
        </h2>
        <div className="space-y-3">
          {metrics.recentActivity?.length > 0 ? (
            metrics.recentActivity.map((activity, index) => (
              <Card
                key={index}
                variant="outlined"
                hover
                className="animate-fadeInUp"
                style={{ animationDelay: `${0.9 + index * 0.05}s` }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center text-xl shadow-lg">
                    {activity.icon}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                      {activity.message}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {activity.timestamp}
                    </p>
                  </div>
                  {activity.actionPath && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(activity.actionPath)}
                    >
                      {language === 'fr' ? 'Voir' : 'View'} →
                    </Button>
                  )}
                </div>
              </Card>
            ))
          ) : (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8">
              {language === 'fr' ? 'Aucune activité récente' : 'No recent activity'}
            </p>
          )}
        </div>
      </Card>
    </AdminLayout>
  );
};

export default AdminDashboard;
